// src/components/accounts/AccountsTable.js
// DROP-IN REPLACEMENT
//
// ✅ Slim + modern (smaller font, tighter rows)
// ✅ Restores Edit icon (NOW opens your real AccountsEditPanel)
// ✅ Accepts external search + filters
// ✅ Rules-safe fetching (admin sees all; non-admin uses group intersection chunks)
// ✅ NEW: supports deep-linking via props: openEditAccountId / onOpenEditAccount / onCloseEditAccount
// ✅ NEW: shows Package icon + sample count badge next to account name (green if converted > 0, else blue if samples > 0)
// ✅ NEW: shows Shield icon when account is admin/owner-only (no groups + no end-user assignment/shares)
//
// ✅ NEW (SAFE PERFORMANCE):
// - Admin/Owner: progressive paging (loads first page fast, then loads rest in background)
// - EndUser: UNCHANGED (still uses your existing group chunk logic)
// - Sample count loading is incremental (only counts for newly loaded accounts)
//
// FIX:
// ✅ Territory column shows reliably using:
//    1) account.territoryName (best, rules-safe)
//    2) territoriesById lookup (if readable)
//    3) fallback label if ID exists but name unavailable
//
// Props:
// - companyId (required)
// - userProfile (optional but recommended)
// - search (string)
// - filters { territoryId: "all", assignedToId: "all" }
// - refreshKey (optional) bump this to force refetch
// - onChanged() optional callback to refresh parent
// - openEditAccountId (optional) if set, auto-opens AccountsEditPanel for that account id
// - onOpenEditAccount(accountId) optional callback (recommended: sets URL ?edit=)
// - onCloseEditAccount() optional callback (recommended: clears URL ?edit=)

import React, { useEffect, useMemo, useRef, useState } from "react";
import { getAuth } from "firebase/auth";
import {
  getFirestore,
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  orderBy,
  limit,
  startAfter,
  documentId,
} from "firebase/firestore";
import { Pencil, Package, Shield, UserCheck } from "lucide-react";

import AccountsEditPanel from "./AccountsEditPanel";
import { buildMembershipAccountCounts, loadCompanyCustomerMemberships } from "../../auth/customerMemberships";

const STATUS_CONVERTED = "Converted to Order";

// ✅ Admin paging size (tune anytime)
const ADMIN_PAGE_SIZE = 250;

const chunk = (arr, size) => {
  const out = [];
  const a = Array.isArray(arr) ? arr : [];
  for (let i = 0; i < a.length; i += size) out.push(a.slice(i, i + size));
  return out;
};

const safeArray = (v) => (Array.isArray(v) ? v : []);
const normLower = (v) => String(v ?? "").trim().toLowerCase();
const normalizeRole = (role) => {
  const r = normLower(role);
  if (["enduser", "end_user", "end-user", "end user", "user"].includes(r)) {
    return "enduser";
  }
  if (r === "owner" || r === "admin" || r === "customer") return r;
  return r;
};

// Robust boolean coercion (handles legacy string values, undefined, etc.)
const asBool = (v) => {
  if (v === true) return true;
  if (v === false) return false;
  if (typeof v === "string") return v.trim().toLowerCase() === "true";
  return false;
};

const getAssignedInternalUserId = (acc = {}) =>
  acc.assignedToId ||
  acc.assignedUserId ||
  acc.repUserId ||
  acc.salesRepId ||
  acc.userId ||
  null;

const isAssignedToUser = (acc = {}, uid) => {
  const me = String(uid || "").trim();
  if (!me) return false;

  const candidates = [
    getAssignedInternalUserId(acc),
    ...(Array.isArray(acc.assignedToUserIds) ? acc.assignedToUserIds : []),
    ...(Array.isArray(acc.assignedUserIds) ? acc.assignedUserIds : []),
    ...(Array.isArray(acc.assignedTo) ? acc.assignedTo : []),
  ]
    .filter(Boolean)
    .map((value) => String(value).trim());

  return candidates.includes(me);
};

const getUserLabel = (u) =>
  u?.name || u?.displayName || u?.fullName || u?.email || "User";

const getAccountNumber = (acc = {}) =>
  acc.accountNumber ||
  acc.accountNo ||
  acc.account_no ||
  acc.externalId ||
  acc.customerNumber ||
  "";

const getAccountCity = (acc = {}) =>
  acc.address?.city || acc.addressCity || acc.city || "";
const getAccountState = (acc = {}) =>
  acc.address?.state || acc.addressState || acc.state || "";

// ✅ Normalizes territoryId from multiple schemas/older data
const getAccountTerritoryId = (acc = {}) => {
  const raw =
    acc.territoryId ??
    acc.territoryID ??
    acc.territory_id ??
    acc.territory?.id ??
    acc.territory?.territoryId ??
    acc.territoryRef?.id ??
    acc.territory ??
    null;

  if (!raw) return null;

  if (typeof raw === "string") return raw.trim() || null;
  if (typeof raw === "number") return String(raw);

  // doc ref or object with id
  if (typeof raw === "object") {
    if (raw.id) return String(raw.id);
    if (raw.path && typeof raw.path === "string") {
      const parts = raw.path.split("/").filter(Boolean);
      return parts.length ? parts[parts.length - 1] : null;
    }
  }

  try {
    return String(raw);
  } catch {
    return null;
  }
};

// ✅ Best-effort territory label that works even if user can't read territories collection
const getTerritoryLabel = (acc = {}, territoriesById) => {
  // 1) Prefer saved label on the account (most reliable + rules-safe)
  const saved =
    (acc.territoryName || acc.territory_label || acc.territoryLabel || "").trim();
  if (saved) return saved;

  // 2) Otherwise try to resolve via ID lookup
  const tid = getAccountTerritoryId(acc);
  if (!tid) return "—";

  const terr = territoriesById?.get ? territoriesById.get(String(tid)) : null;

  // 3) Fallback to something visible if rules block reading territories
  return terr?.name || terr?.label || String(tid) || "Territory";
};

const getSampleAccountId = (data = {}) =>
  data.accountId ||
  data.accountID ||
  data.customerId ||
  (data.accountRef && data.accountRef.id) ||
  null;

// ✅ Detect “admin/owner only” accounts (no groups + no end-user assignment/shares)
const isAdminOwnerOnlyAccount = (acc = {}) => {
  const vis = String(acc.visibility || "").trim().toLowerCase();
  // If explicitly public/all, it’s not admin-only
  if (vis === "all" || vis === "public" || vis === "everyone") return false;

  // If explicitly flagged as internal/admin-only
  const explicit =
    asBool(acc.adminOnly) ||
    asBool(acc.adminsOnly) ||
    asBool(acc.internalOnly) ||
    asBool(acc.staffOnly) ||
    asBool(acc.hiddenFromEndUsers);
  if (explicit) return true;

  // If groups exist, it’s shared (not admin-only)
  const gids = safeArray(acc.groupIds).filter(Boolean);
  if (gids.length > 0) return false;

  // If it’s shared to specific end users (common patterns), it’s not admin-only
  const sharedArrays = []
    .concat(safeArray(acc.sharedWithUserIds))
    .concat(safeArray(acc.visibleToUserIds))
    .concat(safeArray(acc.endUserIds))
    .concat(safeArray(acc.customerUserIds))
    .concat(safeArray(acc.portalUserIds))
    .filter(Boolean);

  if (sharedArrays.length > 0) return false;

  const singleAssignedEndUser =
    acc.customerUserId ||
    acc.endUserId ||
    acc.assignedEndUserId ||
    acc.portalUserId ||
    acc.visibleToUserId ||
    null;

  if (singleAssignedEndUser) return false;

  // Otherwise: no groups + no end-user share/assignment → admin/owner-only
  return true;
};

const hasPortalAccessFlag = (acc = {}) =>
  !!(
    acc.portalUserId ||
    acc.activePortalUserId ||
    acc.customerUserId ||
    acc.primaryCustomerUserId ||
    acc.hasPortalAccess === true ||
    acc.portalAccessActive === true ||
    safeArray(acc.portalUserIds).length
  );

const AccountsTable = ({
  companyId,
  userProfile = null,
  search = "",
  filters,
  accountColumns = [],
  columnVisibility = {},
  refreshKey,
  onChanged,
  selectedAccountIds = [],
  onSelectedAccountIdsChange,
  onVisibleAccountsChange,

  // ✅ NEW: deep-link props
  openEditAccountId = null,
  onOpenEditAccount,
  onCloseEditAccount,
}) => {
  const db = getFirestore();
  const auth = getAuth();

  const [me, setMe] = useState(userProfile);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false); // ✅ admin paging indicator
  const [err, setErr] = useState("");

  const [usersById, setUsersById] = useState(new Map());
  const [territoriesById, setTerritoriesById] = useState(new Map());
  const [portalUsersByAccount, setPortalUsersByAccount] = useState(new Map());

  // ✅ sample counts
  const [sampleCountsByAccount, setSampleCountsByAccount] = useState({});
  const [convertedCountsByAccount, setConvertedCountsByAccount] = useState({});
  const [samplesLoading, setSamplesLoading] = useState(false);
  const [samplesErr, setSamplesErr] = useState("");

  // ✅ Use YOUR real edit panel
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // Track whether current open came from URL (so closing clears URL)
  const editSourceRef = useRef("local"); // "local" | "url"

  // ✅ cancel / de-dupe for loads (prevents race conditions)
  const loadRunIdRef = useRef(0);

  // ✅ track admin paging mode + cursor
  const adminOrderModeRef = useRef("name"); // "name" | "docId"
  const adminCursorRef = useRef(null);

  // ✅ incremental sample counts (avoid re-counting on paging)
  const countedAccountIdsRef = useRef(new Set());

  const myRole = normalizeRole(me?.role);
  const isAdmin = myRole === "admin" || myRole === "owner";
  const canBulkEdit = myRole === "admin" || myRole === "owner";
  const myUid = auth.currentUser?.uid || me?.uid || me?.id || null;
  const selectedAccountIdSet = useMemo(
    () => new Set((Array.isArray(selectedAccountIds) ? selectedAccountIds : []).map((id) => String(id))),
    [selectedAccountIds]
  );
  const visibleAccountColumns = useMemo(
    () => (Array.isArray(accountColumns) ? accountColumns : []).filter((col) => columnVisibility[col.id] !== false),
    [accountColumns, columnVisibility]
  );

  // If userProfile not passed, load it
  useEffect(() => {
    if (userProfile) {
      setMe(userProfile);
      return;
    }

    const run = async () => {
      const uid = auth.currentUser?.uid || null;
      if (!uid) return;

      try {
        const snap = await getDoc(doc(db, "users", uid));
        setMe(snap.exists() ? snap.data() : null);
      } catch (e) {
        console.warn("[AccountsTable] Failed to load my profile:", e);
        setMe(null);
      }
    };

    run();
  }, [userProfile, auth, db]);

  const loadAccounts = async () => {
    const runId = ++loadRunIdRef.current;

    if (!companyId) {
      setAccounts([]);
      setLoading(false);
      setLoadingMore(false);
      return;
    }

    setErr("");
    setLoading(true);
    setLoadingMore(false);

    // reset admin cursor each full load
    adminCursorRef.current = null;

    try {
      const colRef = collection(db, "companies", companyId, "accounts");

      // ------------------------------------------------------------------
      // ✅ ADMIN/OWNER: progressive paging (FAST initial render)
      // ------------------------------------------------------------------
      if (isAdmin) {
        const fetchAdminPage = async (cursorSnap = null) => {
          // prefer ordering by "name" for stable UX; fallback to documentId() if index missing
          const tryName = adminOrderModeRef.current === "name";

          const buildQ = (mode) => {
            const base =
              mode === "name" ? [orderBy("name")] : [orderBy(documentId())];

            if (cursorSnap) {
              return query(colRef, ...base, startAfter(cursorSnap), limit(ADMIN_PAGE_SIZE));
            }
            return query(colRef, ...base, limit(ADMIN_PAGE_SIZE));
          };

          // attempt query; if index missing for name, fallback to docId ordering
          try {
            const q = buildQ(tryName ? "name" : "docId");
            const snap = await getDocs(q);
            return { snap, mode: tryName ? "name" : "docId" };
          } catch (e) {
            // fallback only if we were trying "name"
            if (tryName) {
              console.warn(
                "[AccountsTable] orderBy('name') failed; falling back to docId ordering (no index needed).",
                e
              );
              adminOrderModeRef.current = "docId";
              const snap = await getDocs(buildQ("docId"));
              return { snap, mode: "docId" };
            }
            throw e;
          }
        };

        // 1) first page
        const first = await fetchAdminPage(null);
        if (loadRunIdRef.current !== runId) return;

        const firstDocs = first.snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setAccounts(firstDocs);

        // cursor
        const last = first.snap.docs[first.snap.docs.length - 1] || null;
        adminCursorRef.current = last;

        // show UI immediately
        setLoading(false);

        // 2) background pages
        if (first.snap.size === ADMIN_PAGE_SIZE && last) {
          setLoadingMore(true);

          // keep loading until smaller than page size
          let cursor = last;

          while (cursor) {
            // if a new load kicked off, stop
            if (loadRunIdRef.current !== runId) return;

            const next = await fetchAdminPage(cursor);
            if (loadRunIdRef.current !== runId) return;

            const batch = next.snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            if (batch.length === 0) break;

            setAccounts((prev) => {
              // guard against duplicates
              const map = new Map((prev || []).map((a) => [a.id, a]));
              batch.forEach((a) => map.set(a.id, a));
              return Array.from(map.values());
            });

            const nextLast = next.snap.docs[next.snap.docs.length - 1] || null;
            adminCursorRef.current = nextLast;

            if (!nextLast || next.snap.size < ADMIN_PAGE_SIZE) break;
            cursor = nextLast;
          }

          if (loadRunIdRef.current === runId) setLoadingMore(false);
        }

        return;
      }

      if (!myUid) {
        setAccounts([]);
        return;
      }

      const results = await Promise.allSettled([
        getDocs(query(colRef, where("assignedToId", "==", myUid))),
        getDocs(query(colRef, where("assignedUserId", "==", myUid))),
        getDocs(query(colRef, where("repUserId", "==", myUid))),
        getDocs(query(colRef, where("salesRepId", "==", myUid))),
      ]);

      const map = new Map();
      results.forEach((result) => {
        if (result.status !== "fulfilled") return;
        result.value.docs.forEach((d) => {
          const account = { id: d.id, ...d.data() };
          if (isAssignedToUser(account, myUid)) {
            map.set(d.id, account);
          }
        });
      });

      setAccounts(Array.from(map.values()));
    } catch (e) {
      console.error("[AccountsTable] Failed to load accounts:", e);
      setErr("Failed to load accounts.");
      setAccounts([]);
    } finally {
      // If admin paging, we already setLoading(false) after first page.
      if (loadRunIdRef.current === runId) {
        setLoading(false);
      }
    }
  };

  // Load accounts (rules-safe) + allow parent refreshKey to force reload
  useEffect(() => {
    loadAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, isAdmin, myUid, refreshKey]);

  useEffect(() => {
    if (!onSelectedAccountIdsChange) return;
    const visibleIds = new Set((accounts || []).map((account) => String(account.id)));
    const nextSelected = (Array.isArray(selectedAccountIds) ? selectedAccountIds : []).filter((id) =>
      visibleIds.has(String(id))
    );

    if (nextSelected.length !== (selectedAccountIds || []).length) {
      onSelectedAccountIdsChange(nextSelected);
    }
  }, [accounts, onSelectedAccountIdsChange, selectedAccountIds]);

  // Load users map (for Assigned column label)
  useEffect(() => {
    if (!companyId) return;

    const run = async () => {
      try {
        const uid = auth.currentUser?.uid || null;
        if (!uid) {
          setUsersById(new Map());
          return;
        }

        if (!isAdmin) {
          const m = new Map();
          m.set(uid, { id: uid, ...(me || {}) });
          setUsersById(m);
          return;
        }

        const qUsers = query(collection(db, "users"), where("companyId", "==", companyId));
        const snap = await getDocs(qUsers);
        const m = new Map();
        snap.docs.forEach((d) => {
          const u = d.data() || {};
          if (String(u.role || "").toLowerCase() === "customer") return;
          m.set(d.id, { id: d.id, ...u });
        });
        setUsersById(m);
      } catch (e) {
        console.warn("[AccountsTable] Failed to load users:", e);
        setUsersById(new Map());
      }
    };

    run();
  }, [companyId, db, auth, isAdmin, me]);

  // Load territories map (for labels if readable)
  useEffect(() => {
    if (!companyId) return;

    const run = async () => {
      try {
        const snap = await getDocs(collection(db, "companies", companyId, "territories"));
        const m = new Map();
        snap.docs.forEach((d) => m.set(d.id, { id: d.id, ...d.data() }));
        setTerritoriesById(m);
      } catch (e) {
        // If rules block this for non-admins, that's fine — we fallback to territoryName.
        console.warn("[AccountsTable] Failed to load territories (ok if rules):", e);
        setTerritoriesById(new Map());
      }
    };

    run();
  }, [companyId, db]);

  useEffect(() => {
    if (!companyId) {
      setPortalUsersByAccount(new Map());
      return;
    }

    let cancelled = false;

    const run = async () => {
      try {
        const memberships = await loadCompanyCustomerMemberships(db, companyId);
        if (cancelled) return;
        const next = buildMembershipAccountCounts(memberships);
        setPortalUsersByAccount(next);
      } catch (e) {
        if (e?.code !== "permission-denied") {
          console.warn("[AccountsTable] Failed to load portal user indicators:", e);
        }
        if (!cancelled) setPortalUsersByAccount(new Map());
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [companyId, db, refreshKey]);

  // ✅ Reset sample count cache on company/refresh
  useEffect(() => {
    countedAccountIdsRef.current = new Set();
    setSampleCountsByAccount({});
    setConvertedCountsByAccount({});
    setSamplesErr("");
    setSamplesLoading(false);
  }, [companyId, refreshKey]);

  // ✅ Incremental: only load sample counts for newly-loaded accountIds
  useEffect(() => {
    if (!companyId) return;

    const run = async () => {
      const ids = (accounts || []).map((a) => a.id).filter(Boolean);
      if (!ids.length) return;

      const newIds = ids.filter((id) => !countedAccountIdsRef.current.has(id));
      if (!newIds.length) return;

      // mark as counted immediately to prevent duplicate in-flight loops
      newIds.forEach((id) => countedAccountIdsRef.current.add(id));

      setSamplesErr("");
      setSamplesLoading(true);

      try {
        const colRef = collection(db, "companies", companyId, "sampleOrders");

        const batchCounts = {};
        const batchConverted = {};

        const chunks = chunk(newIds, 10);

        for (const c of chunks) {
          if (!c.length) continue;

          // NOTE: assumes sampleOrders has field "accountId"
          const qReq = query(colRef, where("accountId", "in", c));
          const snap = await getDocs(qReq);

          snap.docs.forEach((d) => {
            const data = d.data() || {};
            const accId = getSampleAccountId(data);
            if (!accId) return;

            batchCounts[accId] = (batchCounts[accId] || 0) + 1;

            const status = String(data.status || "");
            const isConverted =
              status === STATUS_CONVERTED ||
              data.convertedToOrder === true ||
              !!data.orderNumber;

            if (isConverted) batchConverted[accId] = (batchConverted[accId] || 0) + 1;
          });
        }

        setSampleCountsByAccount((prev) => {
          const next = { ...(prev || {}) };
          Object.keys(batchCounts).forEach((accId) => {
            next[accId] = (next[accId] || 0) + (batchCounts[accId] || 0);
          });
          return next;
        });

        setConvertedCountsByAccount((prev) => {
          const next = { ...(prev || {}) };
          Object.keys(batchConverted).forEach((accId) => {
            next[accId] = (next[accId] || 0) + (batchConverted[accId] || 0);
          });
          return next;
        });
      } catch (e) {
        console.warn("[AccountsTable] Failed to load sample counts:", e);
        setSamplesErr("Could not load sample counts.");
      } finally {
        setSamplesLoading(false);
      }
    };

    run();
  }, [companyId, db, accounts]);

  // Apply external search + filters
  const filtered = useMemo(() => {
    let list = Array.isArray(accounts) ? [...accounts] : [];

    const territoryId = filters?.territoryId || "all";
    const assignedToId = filters?.assignedToId || "all";

    if (territoryId !== "all") {
      list = list.filter((a) => String(getAccountTerritoryId(a) || "") === String(territoryId));
    }

    if (assignedToId !== "all") {
      list = list.filter((a) => String(getAssignedInternalUserId(a) || "") === String(assignedToId));
    }

    const q = normLower(search);
    if (q) {
      list = list.filter((a) => {
        const name = normLower(a.name || a.accountName || "");
        const num = normLower(getAccountNumber(a));
        const city = normLower(getAccountCity(a));
        const state = normLower(getAccountState(a));
        const email = normLower(a.email || a.contactEmail || a.primaryContact?.email || "");
        const phone = normLower(a.phone || a.locationPhone || "");
        const terr = normLower(a.territoryName || "");
        return (
          name.includes(q) ||
          num.includes(q) ||
          city.includes(q) ||
          state.includes(q) ||
          email.includes(q) ||
          phone.includes(q) ||
          terr.includes(q)
        );
      });
    }

    list.sort((a, b) => {
      const an = String(a.name || a.accountName || "").toLowerCase();
      const bn = String(b.name || b.accountName || "").toLowerCase();
      return an.localeCompare(bn);
    });

    return list;
  }, [accounts, filters, search]);

  const renderSampleBadge = (accountId) => {
    const samples = Number(sampleCountsByAccount?.[accountId] || 0);
    const conv = Number(convertedCountsByAccount?.[accountId] || 0);

    if (conv > 0) {
      return (
        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-green-50 text-green-700 whitespace-nowrap border border-green-200">
          <Package className="w-3 h-3" />
          {conv}
        </span>
      );
    }

    if (samples > 0) {
      return (
        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 whitespace-nowrap border border-blue-200">
          <Package className="w-3 h-3" />
          {samples}
        </span>
      );
    }

    return null;
  };

  const filteredIds = useMemo(() => filtered.map((account) => String(account.id)), [filtered]);

  useEffect(() => {
    onVisibleAccountsChange?.(filtered);
  }, [filtered, onVisibleAccountsChange]);
  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selectedAccountIdSet.has(id));

  const toggleAccountSelection = (accountId) => {
    if (!onSelectedAccountIdsChange) return;
    const id = String(accountId || "").trim();
    if (!id) return;

    const set = new Set(selectedAccountIdSet);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    onSelectedAccountIdsChange(Array.from(set));
  };

  const toggleSelectAllFiltered = () => {
    if (!onSelectedAccountIdsChange) return;

    const set = new Set(selectedAccountIdSet);
    if (allFilteredSelected) {
      filteredIds.forEach((id) => set.delete(id));
    } else {
      filteredIds.forEach((id) => set.add(id));
    }
    onSelectedAccountIdsChange(Array.from(set));
  };

  const openEdit = async (accountOrId, source = "local") => {
    const id = typeof accountOrId === "string" ? accountOrId : accountOrId?.id;
    if (!id) return;

    editSourceRef.current = source;

    // Prefer existing object if we have it
    const local = typeof accountOrId === "object" && accountOrId ? accountOrId : null;
    const fromList = local || (accounts || []).find((a) => a.id === id) || null;

    if (fromList) {
      setEditing(fromList);
      setEditOpen(true);
      return;
    }

    // Fallback: fetch the doc directly (may fail if rules disallow)
    try {
      const snap = await getDoc(doc(db, "companies", companyId, "accounts", id));
      if (snap.exists()) {
        setEditing({ id: snap.id, ...snap.data() });
        setEditOpen(true);
      } else {
        setErr("This account link is invalid (account not found).");
      }
    } catch (e) {
      console.warn("[AccountsTable] openEdit fetch failed:", e);
      setErr("You don’t have permission to open that account (or it no longer exists).");
    }
  };

  const getAccountHeaderClass = (colId) => {
    const base = "px-3 py-2 text-left text-[12px] font-semibold text-slate-800 border-r border-sky-100 last:border-r-0 whitespace-nowrap";
    if (colId === "select") return `${base} w-[44px]`;
    if (colId === "actions") return `${base} text-right w-[56px]`;
    return base;
  };

  const getAccountCellClass = (colId) => {
    const base = "px-3 py-2 align-top border-r border-slate-100 last:border-r-0";
    if (colId === "actions") return `${base} text-right`;
    return base;
  };

  const renderAccountHeader = (col) => {
    if (col.id === "select") {
      return canBulkEdit ? (
        <input
          type="checkbox"
          checked={allFilteredSelected}
          onChange={toggleSelectAllFiltered}
          aria-label="Select all visible accounts"
        />
      ) : null;
    }
    if (col.id === "actions") return " ";
    return col.label;
  };

  const renderAccountCell = (col, account) => {
    const number = getAccountNumber(account);
    const name = account.name || account.accountName || "Unnamed";
    const city = getAccountCity(account);
    const state = getAccountState(account);
    const cityState = [city, state].filter(Boolean).join(", ") || "—";
    const terrName = getTerritoryLabel(account, territoriesById);
    const assignedId = getAssignedInternalUserId(account);
    const assignedUser = assignedId ? usersById.get(assignedId) : null;
    const assignedLabel = assignedUser ? getUserLabel(assignedUser) : "—";
    const email = account.email || account.contactEmail || account.primaryContact?.email || "";
    const phone = account.phone || account.locationPhone || "";
    const adminOnly = isAdminOwnerOnlyAccount(account);
    const portalActive =
      hasPortalAccessFlag(account) || (portalUsersByAccount.get(String(account.id)) || 0) > 0;
    const sampleCount = Number(sampleCountsByAccount?.[account.id] || 0);
    const convertedCount = Number(convertedCountsByAccount?.[account.id] || 0);

    switch (col.id) {
      case "select":
        return canBulkEdit ? (
          <input
            type="checkbox"
            checked={selectedAccountIdSet.has(String(account.id))}
            onChange={() => toggleAccountSelection(account.id)}
            aria-label={`Select ${name}`}
          />
        ) : null;
      case "account":
        return (
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-start gap-2 min-w-0">
                <div className="flex items-center gap-1 shrink-0 pt-0.5">
                  {adminOnly ? (
                    <span
                      className="inline-flex items-center justify-center h-6 w-6 rounded-md border border-slate-200 bg-slate-50 text-slate-700"
                      title="Owners and Admins only"
                    >
                      <Shield className="w-3.5 h-3.5" />
                    </span>
                  ) : null}
                  {portalActive ? (
                    <span
                      className="inline-flex items-center justify-center h-6 w-6 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700"
                      title="Active customer portal account"
                    >
                      <UserCheck className="w-3.5 h-3.5" />
                    </span>
                  ) : null}
                </div>

                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 leading-tight truncate">{name}</div>
                  {number ? <div className="text-[11px] text-slate-500 leading-tight">#{number}</div> : null}
                </div>
              </div>
            </div>

            <div className="flex-shrink-0 pt-0.5">{renderSampleBadge(account.id)}</div>
          </div>
        );
      case "accountNumber":
        return <div className="text-slate-700 leading-tight">{number || "—"}</div>;
      case "city":
        return <div className="text-slate-700 leading-tight">{cityState}</div>;
      case "territory":
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-slate-100 text-slate-700 border border-slate-200">
            {terrName}
          </span>
        );
      case "assigned":
        return <div className="text-slate-700 leading-tight">{assignedLabel}</div>;
      case "contact":
        return (
          <div className="text-slate-700 leading-tight">
            <div className="leading-tight">{email || "—"}</div>
            {phone ? <div className="text-[11px] text-slate-500">{phone}</div> : null}
          </div>
        );
      case "samples":
        return (
          <div className="text-slate-700 leading-tight">
            <div>{sampleCount}</div>
            {convertedCount > 0 ? <div className="text-[11px] text-emerald-600">{convertedCount} converted</div> : null}
          </div>
        );
      case "portal":
        return (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] border ${
              portalActive
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : "border-slate-200 bg-slate-50 text-slate-500"
            }`}
          >
            {portalActive ? "Active" : "None"}
          </span>
        );
      case "actions":
        return (
          <button
            type="button"
            onClick={() => {
              if (onOpenEditAccount) {
                onOpenEditAccount(account.id);
                return;
              }
              openEdit(account, "local");
            }}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-slate-100 text-slate-600 hover:text-slate-900"
            title="Edit"
          >
            <Pencil className="w-4 h-4" />
          </button>
        );
      default:
        return null;
    }
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditing(null);

    // If opened via URL, clear URL param
    if (editSourceRef.current === "url") {
      onCloseEditAccount && onCloseEditAccount();
    }
    editSourceRef.current = "local";
  };

  // ✅ NEW: open edit panel when URL param is present
  useEffect(() => {
    const id = String(openEditAccountId || "").trim();
    if (!id) return;

    // prevent loops: if already open on same id, do nothing
    if (editOpen && editing?.id === id) return;

    openEdit(id, "url");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openEditAccountId, companyId, accounts]);

  return (
    <>
      <div className="accounts-table-shell overflow-hidden bg-white md:rounded-xl md:border md:border-slate-200">
        <div className="hidden md:flex px-3 py-2 border-b border-slate-200 items-center gap-3">
          <div className="text-[13px] font-semibold text-slate-900">Accounts</div>

          <div className="text-[11px] text-slate-500 flex items-center gap-3">
            {loadingMore ? <span>Loading more…</span> : null}
            {samplesLoading ? <span>Loading sample counts…</span> : null}
            {samplesErr ? <span className="text-amber-700">{samplesErr}</span> : null}
          </div>
        </div>

        {err && (
          <div className="px-3 py-2 text-[13px] bg-red-50 border-b border-red-200 text-red-800">
            {err}
          </div>
        )}

        {loading ? (
          <div className="px-3 py-4 text-[13px] text-slate-500">Loading accounts…</div>
        ) : filtered.length === 0 ? (
          <div className="px-3 py-4 text-[13px] text-slate-500">
            No accounts match the current search/filters.
          </div>
        ) : (
          <div className="overflow-auto">
            <div className="divide-y divide-slate-100 md:hidden">
              {filtered.map((a) => {
                const number = getAccountNumber(a);
                const name = a.name || a.accountName || "Unnamed";
                const city = getAccountCity(a);
                const state = getAccountState(a);
                const cityState = [city, state].filter(Boolean).join(", ") || "—";
                const terrName = getTerritoryLabel(a, territoriesById);
                const assignedId = getAssignedInternalUserId(a);
                const assignedUser = assignedId ? usersById.get(assignedId) : null;
                const assignedLabel = assignedUser ? getUserLabel(assignedUser) : "—";
                const email = a.email || a.contactEmail || a.primaryContact?.email || "";
                const phone = a.phone || a.locationPhone || "";
                const adminOnly = isAdminOwnerOnlyAccount(a);
                const portalActive =
                  hasPortalAccessFlag(a) || (portalUsersByAccount.get(String(a.id)) || 0) > 0;
                return (
                  <div key={a.id} className="px-2 py-1">
                    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="truncate text-sm font-semibold text-slate-900">{name}</div>
                            {adminOnly ? (
                              <span className="inline-flex items-center justify-center h-5 w-5 rounded-md border border-slate-200 bg-white text-slate-700">
                                <Shield className="w-3 h-3" />
                              </span>
                            ) : null}
                            {portalActive ? (
                              <span
                                className="inline-flex items-center justify-center h-5 w-5 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700"
                                title="Active customer portal account"
                              >
                                <UserCheck className="w-3 h-3" />
                              </span>
                            ) : null}
                            {renderSampleBadge(a.id)}
                          </div>

                          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-slate-500">
                            {number ? <span className="whitespace-nowrap">#{number}</span> : null}
                            <span className="truncate">{cityState}</span>
                            <span className="truncate">{terrName}</span>
                            <span className="truncate">{assignedLabel}</span>
                            {(email || phone) ? <span className="truncate">{email || phone}</span> : null}
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => {
                            if (onOpenEditAccount) {
                              onOpenEditAccount(a.id);
                              return;
                            }
                            openEdit(a, "local");
                          }}
                          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-slate-50 text-slate-600"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="hidden md:block">
            <table className="min-w-full text-[13px]">
              <thead className="text-[12px]">
                <tr className="border-b border-sky-100">
                  {visibleAccountColumns.map((col) => (
                    <th key={col.id} className={getAccountHeaderClass(col.id)}>
                      {renderAccountHeader(col)}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {filtered.map((a) => {
                  return (
                    <tr key={a.id} className="hover:bg-slate-50">
                      {visibleAccountColumns.map((col) => (
                        <td key={col.id} className={getAccountCellClass(col.id)}>
                          {renderAccountCell(col, a)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>

      <AccountsEditPanel
        isOpen={editOpen}
        onClose={closeEdit}
        companyId={companyId}
        account={editing}
        onUpdated={async () => {
          await loadAccounts();
          onChanged && onChanged();
        }}
      />
    </>
  );
};

export default AccountsTable;
